import { EducationLogo } from '@seek/certsy-ui';
import { CertsyDialog } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import { useCallback, useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsSeekPassDialogCallToAction } from 'src/modules/analytics/types';

import type { VerificationsSeekPassDialogAnalyticsOrigin } from '../analyticsUtils';

import translations from './.vocab';

interface Props {
  url: string;
  showDialog: boolean;
  closeDialogFn: () => void;
  origin: VerificationsSeekPassDialogAnalyticsOrigin;
  onSeekPassDialogClick: (arg: {
    callToAction: AnalyticsVerificationsSeekPassDialogCallToAction;
  }) => void;
}

export const VerifyEducationDialog = ({
  url,
  showDialog,
  closeDialogFn,
  origin,
  onSeekPassDialogClick,
}: Props) => {
  const { t } = useTranslations(translations);

  const { seekPassDialogDisplayed } = useAnalytics();

  const onDialogDisplay = useCallback(() => {
    seekPassDialogDisplayed({ origin, certsyType: 'education' });
  }, [origin, seekPassDialogDisplayed]);

  const onClose = () => {
    closeDialogFn();
  };

  const onCertsyClick = () => {
    onSeekPassDialogClick({
      callToAction: 'verify on certsy',
    });
  };

  const onMaybeLaterClick = () => {
    onSeekPassDialogClick({ callToAction: 'maybe later' });
  };

  useEffect(() => {
    if (showDialog) {
      onDialogDisplay();
    }
  }, [onDialogDisplay, showDialog]);

  return (
    <CertsyDialog
      title={t('Verify your education')}
      bodyText={t('Help your application stand out...')}
      certsyButtonText={t('Verify on SEEK Pass')}
      seekPassLogo={<EducationLogo />}
      certsyUrl={url}
      width="small"
      open={showDialog}
      onCertsyClick={onCertsyClick}
      onMaybeLaterClick={onMaybeLaterClick}
      onClose={onClose}
      data={{ automation: 'education-verify-dialog' }}
    />
  );
};
